/**
 * @page FlexPoolPage
 * @route /flex/pool
 * @description Aktive Flex-Worker Pool — Score-Badges, Verfügbarkeit, Bulk-Select, KPIs.
 * @roles admin
 * @i18n flex.pool.*
 * @endpoints GET /api/flex/pool?suche=&verfuegbarkeit=
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Users, Search, X, TrendingUp, CalendarCheck, ChevronDown, ChevronUp, Star, Globe } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const VERFUEGBARKEIT_MAP = {
  sofort:     { label: 'flex.pool.avail_sofort',    color: '#10B981', bg: '#D1FAE5' },
  flexibel:   { label: 'flex.pool.avail_flexibel',  color: '#338edc', bg: '#EBF4FD' },
  eingeschraenkt: { label: 'flex.pool.avail_eingeschraenkt', color: '#F59E0B', bg: '#FEF3C7' },
};

function ScoreBadge({ score }) {
  const color = score >= 80 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';
  const bg = score >= 80 ? '#D1FAE5' : score >= 50 ? '#FEF3C7' : '#FEE2E2';
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: bg, color }}>
      {score}
    </span>
  );
}

function VerfuegbarkeitChip({ value, t }) {
  const v = VERFUEGBARKEIT_MAP[value];
  if (!v) return <span className="text-xs text-[var(--text-tertiary,#6B7280)]">–</span>;
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: v.bg, color: v.color }}>
      {t(v.label) || value}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function FlexPoolPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [workers, setWorkers] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [suche, setSuche] = useState('');
  const [verfFilter, setVerfFilter] = useState('');

  // Sort
  const [sortKey, setSortKey] = useState('score');
  const [sortDir, setSortDir] = useState('desc');

  // Bulk select
  const [selected, setSelected] = useState(new Set());

  const fetchPool = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (suche) params.set('suche', suche);
      if (verfFilter) params.set('verfuegbarkeit', verfFilter);
      const res = await fetch(`${API}/api/flex/pool?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setWorkers(data.pool || []);
        setKpis(data.kpis || null);
      }
    } catch {} finally {
      setLoading(false);
    }
  }, [suche, verfFilter]);

  useEffect(() => { fetchPool(); }, [fetchPool]);

  const handleSearch = () => { setSuche(searchInput); };
  const clearSearch = () => { setSearchInput(''); setSuche(''); };

  // Sort logic
  const sorted = [...workers].sort((a, b) => {
    let va = a[sortKey], vb = b[sortKey];
    if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'nachname' ? 'asc' : 'desc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={14} className="inline ml-0.5" />
      : <ChevronDown size={14} className="inline ml-0.5" />;
  };

  // Bulk
  const allSelected = workers.length > 0 && selected.size === workers.length;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(workers.map(w => w.id)));
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const kpiCards = kpis ? [
    { label: t('flex.pool.kpi_pool_size') || 'Pool-Größe', value: kpis.pool_size, Icon: Users, iconColor: 'text-[#338edc]' },
    { label: t('flex.pool.kpi_avg_score') || 'Ø Score', value: kpis.avg_score, Icon: TrendingUp, iconColor: 'text-[#10B981]' },
    { label: t('flex.pool.kpi_verfuegbar') || 'Verfügbar heute', value: kpis.verfuegbar_heute, Icon: CalendarCheck, iconColor: 'text-[#6366F1]' },
  ] : [];

  return (
    <div className="p-4 sm:p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold text-[var(--text-primary,#1A1A2E)]">
          {t('flex.pool.title') || 'Flex-Worker Pool'}
        </h1>
        {selected.size > 0 && (
          <span className="text-sm text-[var(--text-secondary,#4B5563)]">
            {selected.size} {t('flex.pool.selected') || 'ausgewählt'}
          </span>
        )}
      </div>

      {/* KPI Cards */}
      {kpis && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {kpiCards.map((k, i) => (
            <div key={i} className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center flex-shrink-0">
                <k.Icon size={18} className={k.iconColor || "text-[var(--text-secondary,#4B5563)]"} />
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary,#4B5563)] font-medium">{k.label}</p>
                <p className="text-2xl font-semibold text-[var(--text-primary,#1A1A2E)]">{k.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={verfFilter}
          onChange={e => setVerfFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm text-[var(--text-primary,#1A1A2E)]"
        >
          <option value="">{t('flex.pool.filter_avail') || 'Verfügbarkeit'}: {t('common.all') || 'Alle'}</option>
          {Object.entries(VERFUEGBARKEIT_MAP).map(([k, v]) => (
            <option key={k} value={k}>{t(v.label) || k}</option>
          ))}
        </select>

        <div className="flex items-center gap-1 flex-1 min-w-[200px] max-w-sm">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary,#6B7280)]" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder={t('common.search') || 'Suche...'}
              className="w-full pl-9 pr-8 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] placeholder-[var(--text-tertiary,#6B7280)]"
            />
            {searchInput && (
              <button onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)]">
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
        </div>
      ) : workers.length === 0 ? (
        <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl p-12 text-center">
          <Users size={40} className="mx-auto text-[var(--text-tertiary,#6B7280)] mb-3" />
          <p className="text-sm text-[var(--text-secondary,#4B5563)]">
            {t('flex.pool.empty') || 'Keine aktiven Flex-Worker gefunden'}
          </p>
        </div>
      ) : (
        /* Table */
        <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-default,#E5E7EB)]">
                  <th className="px-4 py-3 text-left w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll}
                      className="rounded border-[var(--border-default,#E5E7EB)]" />
                  </th>
                  <th onClick={() => toggleSort('nachname')} className="px-4 py-3 text-left text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider cursor-pointer select-none">
                    Name <SortIcon col="nachname" />
                  </th>
                  <th onClick={() => toggleSort('score')} className="px-4 py-3 text-center text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider cursor-pointer select-none">
                    Score <SortIcon col="score" />
                  </th>
                  <th onClick={() => toggleSort('bewertung_avg')} className="px-4 py-3 text-center text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider cursor-pointer select-none hidden sm:table-cell">
                    {t('flex.rating.title') || 'Bewertung'} <SortIcon col="bewertung_avg" />
                  </th>
                  <th onClick={() => toggleSort('einsaetze')} className="px-4 py-3 text-center text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider cursor-pointer select-none hidden sm:table-cell">
                    {t('flex.pool.col_einsaetze') || 'Einsätze'} <SortIcon col="einsaetze" />
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider hidden md:table-cell">
                    {t('flex.pool.col_verfuegbarkeit') || 'Verfügbarkeit'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider hidden lg:table-cell">
                    {t('flex.pool.col_einschulung') || 'Einschulung'}
                  </th>
                  <th onClick={() => toggleSort('zusage_pct')} className="px-4 py-3 text-center text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider cursor-pointer select-none hidden lg:table-cell">
                    {t('flex.pool.col_zusage') || 'Zusage %'} <SortIcon col="zusage_pct" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(w => (
                  <tr key={w.id}
                    className="border-b border-[var(--border-default,#E5E7EB)] last:border-0 hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors cursor-pointer"
                    onClick={() => navigate(`/recruiting/bewerber/${w.id}?typ=flex`)}
                  >
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(w.id)} onChange={() => toggleOne(w.id)}
                        className="rounded border-[var(--border-default,#E5E7EB)]" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 font-medium text-[var(--text-primary,#1A1A2E)]">
                        {w.vorname} {w.nachname}
                        {w.odoo_employee_id && <Globe size={12} className="text-green-500 flex-shrink-0" title={`Odoo HR #${w.odoo_employee_id}`} />}
                      </div>
                      <div className="text-xs text-[var(--text-secondary,#4B5563)]">{w.email}</div>
                    </td>
                    <td className="px-4 py-3 text-center"><ScoreBadge score={w.score} /></td>
                    <td className="px-4 py-3 text-center hidden sm:table-cell">
                      {w.bewertung_avg != null ? (
                        <div className="inline-flex items-center gap-1">
                          <Star size={13} fill="#F59E0B" className="text-amber-400" />
                          <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{parseFloat(w.bewertung_avg).toFixed(1)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--text-muted,#9CA3AF)]">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center hidden sm:table-cell">
                      <span className="text-[var(--text-primary,#1A1A2E)]">{w.einsaetze}</span>
                      {w.no_shows > 0 && (
                        <span className="ml-1 text-xs text-[#EF4444]">({w.no_shows} NS)</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center hidden md:table-cell">
                      <VerfuegbarkeitChip value={w.verfuegbarkeit} t={t} />
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary,#4B5563)] hidden lg:table-cell">
                      {formatDate(w.einschulung_datum)}
                    </td>
                    <td className="px-4 py-3 text-center hidden lg:table-cell">
                      <span className="text-[var(--text-primary,#1A1A2E)]">{w.zusage_pct}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
